import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import toast from 'react-hot-toast';

import { Breadcrumb, Loader, CommunitySlotsModal } from 'components';
import {
  getPendingCommunitySubscriptionSlots,
  setPendingCommunitySubscriptionSlots,
} from 'utils/pendingCommunitySubscriptionSlots';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import {
  buildStripeCheckoutPresentmentBody,
  createOnboardingCheckoutSessionRequest,
} from 'services/subscriptions';
import { getLocation } from 'services/geolocation';
import {
  getAllSubscriptions,
  getCurrentUserSubscription,
} from 'store/actionCreators/subscriptions';
import { getAuthUser } from 'store/selectors/auth';
import { getCommunities } from 'store/selectors/communities';
import {
  selectAllOnboardingPlans,
  selectAllSubscriptions,
  selectCurrentUserSubscription,
  selectHasPlansData,
  selectIsPlansInitialLoading,
  selectIsSubscriptionLoading,
} from 'store/selectors/subscriptions';
import {
  OnboardingCatalogItem,
  SubscriptionFrequency,
  SubscriptionPlan,
  SubscriptionStatus,
  UserSubscription,
} from 'types';
import { formatSubscriptionDate } from 'utils/dates';
import {
  formatMoney,
  formatMoneyAmount,
  getMoneyLocale,
  planPriceLeadingSymbol,
} from 'utils/money';

import { FeatureMatrix } from './FeatureMatrix';
import * as S from './styled';
import { OutsideAppPageContentWrapper } from 'components/Shared/styled';

const TRIAL_DEFAULTS = {
  maxCommunities: 30,
  maxGroups: 600,
  maxMembers: 3000,
  maxStorage: 15,
} as const;

const USAGE_METRIC_ICONS = {
  members: '👥',
  communities: '🏠',
  schedulers: '📋',
  storage: '💾',
} as const;

const pct = (used: number, max: number) => {
  if (!max || max <= 0) return 0;
  return Math.min(100, Math.round((used / max) * 100));
};

/** Stripe-backed one-time amount from `plans/v2` `onboarding` (major units + ISO currency). */
const formatOnboardingOneTime = (
  row: OnboardingCatalogItem | undefined,
): string | null => {
  if (!row || row.oneTimeValue == null) return null;
  const code = (row.currency || 'CHF').toUpperCase();
  try {
    return new Intl.NumberFormat(getMoneyLocale(code), {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(row.oneTimeValue);
  } catch {
    return `${row.oneTimeValue} ${code}`;
  }
};

/** Renders a setup bullet that may contain a bold title and sub-description separated by " - ". */
const SetupBullet = ({ text }: { text: string }) => {
  const sepIdx = text.indexOf(' - ');
  if (sepIdx > -1) {
    const title = text.slice(0, sepIdx);
    const sub = text.slice(sepIdx + 3);
    return (
      <S.SetupLiText>
        <strong>{title}</strong>
        <S.SetupLiSub>{sub}</S.SetupLiSub>
      </S.SetupLiText>
    );
  }
  return <S.SetupLiText>{text}</S.SetupLiText>;
};

const partitionPlans = (plans: SubscriptionPlan[]) => {
  const rest = plans.filter((p) => !/enterprise/i.test(p.name));
  const sorted = [...rest].sort(
    (a, b) =>
      a.maxCommunities - b.maxCommunities ||
      a.maxMembers - b.maxMembers ||
      a.yearlyValue - b.yearlyValue,
  );
  const base = sorted[0];
  const tiers = sorted.slice(1).filter((p) => p.id !== base?.id);
  return { base, tiers };
};

const planFits = (
  plan: SubscriptionPlan,
  used: {
    communities: number;
    activeCommunities: number;
    members: number;
    groups: number;
    storage: number;
  },
) =>
  used.activeCommunities <= plan.maxCommunities &&
  used.members <= plan.maxMembers &&
  used.groups <= plan.maxGroups &&
  used.storage <= plan.maxStorage;

const ChangePlanPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const subscription = useSelector(selectCurrentUserSubscription);
  const isSubscriptionLoading = useSelector(selectIsSubscriptionLoading);
  const profile = useSelector(getAuthUser);
  const communities = useSelector(getCommunities);
  const [hasObservedSubscriptionLoad, setHasObservedSubscriptionLoad] =
    useState(false);

  const plans = useSelector(selectAllSubscriptions);
  const onboardingPlans = useSelector(selectAllOnboardingPlans);
  const hasPlansData = useSelector(selectHasPlansData);
  const isPlansInitialLoading = useSelector(selectIsPlansInitialLoading);
  /** Geo used for catalog - reused so Checkout currency matches displayed prices. */
  const [catalogGeo, setCatalogGeo] = useState<Awaited<
    ReturnType<typeof getLocation>
  > | null>(null);
  const [onboardingCheckoutSlug, setOnboardingCheckoutSlug] = useState<
    string | null
  >(null);

  const [yearly, setYearly] = useState(true);
  const [tierIdx, setTierIdx] = useState(0);
  const [communitySlotsOpen, setCommunitySlotsOpen] = useState(false);
  const [communitySlotsMax, setCommunitySlotsMax] = useState(0);
  const pendingCheckoutRef = useRef<{
    href: string;
    plan: SubscriptionPlan;
  } | null>(null);

  const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;
  const checkoutBase = `/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;
  const enterpriseCheckoutPath = `/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.ENTERPRISE_CHECKOUT}`;

  const groupsCount = useMemo(() => {
    if (communities.length > 0 && profile) {
      const owned = communities.filter(
        (item) => profile?.userId === item?.communityOwner?.userId,
      );
      return owned.reduce(
        (acc, item) =>
          acc +
          Math.max(Number(item.groupsCount) || 0, item.groups?.length || 0),
        0,
      );
    }
    return 0;
  }, [communities, profile]);
  const ownedCommunities = useMemo(() => {
    if (!profile?.userId || communities.length === 0) return [];
    return communities.filter(
      (item) => profile.userId === item.communityOwner?.userId,
    );
  }, [communities, profile?.userId]);
  const ownedUsersCount = useMemo(
    () =>
      ownedCommunities.reduce(
        (acc, item) => acc + (Number(item.usersCount) || 0),
        0,
      ),
    [ownedCommunities],
  );
  const ownedCommunitiesCount = ownedCommunities.length;

  const isTrial =
    Boolean(subscription?.id) &&
    subscription?.planId === SubscriptionStatus.TRIAL;

  const isCancelled =
    subscription?.status === SubscriptionStatus.CANCELLED &&
    subscription?.subscriptionId !== SubscriptionStatus.MIGRATED;

  const isPaidActive =
    Boolean(subscription?.id) &&
    subscription?.planId !== SubscriptionStatus.TRIAL &&
    !isCancelled;

  const subscriptionEndDate = useMemo(() => {
    if (!subscription?.endDate) return '-';
    return formatSubscriptionDate(subscription.endDate, i18n.language);
  }, [subscription, i18n.language]);

  /** Omit invalid dates so renewal lines are not blank or placeholder-only. */
  const renewalDateFormatted = useMemo(() => {
    if (!subscription?.endDate) return null;
    const formatted = formatSubscriptionDate(
      subscription.endDate,
      i18n.language,
    );
    return formatted === '-' ? null : formatted;
  }, [subscription?.endDate, i18n.language]);

  const maxMembers = subscription?.maxMembers || TRIAL_DEFAULTS.maxMembers;
  const maxCommunities =
    subscription?.maxCommunities || TRIAL_DEFAULTS.maxCommunities;
  const maxGroups = subscription?.maxGroups || TRIAL_DEFAULTS.maxGroups;
  const maxStorage = subscription?.maxStorage || TRIAL_DEFAULTS.maxStorage;

  const u = subscription?.info;
  const usedMembers = Math.max(u?.users ?? 0, ownedUsersCount);
  const usedCommunities = Math.max(u?.communities ?? 0, ownedCommunitiesCount);
  const ownedActiveCommunitiesCount = useMemo(() => {
    if (!profile?.userId || communities.length === 0) return 0;
    return communities.filter(
      (item) =>
        item.communityOwner?.userId === profile.userId &&
        item.subscriptionSlotActive,
    ).length;
  }, [communities, profile?.userId]);
  const usedActiveCommunities = Math.max(
    u?.activeCommunities ?? 0,
    usedCommunities,
    ownedActiveCommunitiesCount,
  );
  const usedGroups = Math.max(u?.groups ?? 0, groupsCount);
  const usedStorage = u?.bucketSize ?? 0;

  const used = useMemo(
    () => ({
      communities: usedCommunities,
      activeCommunities: usedActiveCommunities,
      members: usedMembers,
      groups: usedGroups,
      storage: usedStorage,
    }),
    [
      usedCommunities,
      usedActiveCommunities,
      usedMembers,
      usedGroups,
      usedStorage,
    ],
  );

  useEffect(() => {
    dispatch(getAllSubscriptions.trigger({ silent: false }));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let location: Awaited<ReturnType<typeof getLocation>> | null = null;
      try {
        location = await getLocation();
      } catch {
        location = { country_name: 'Switzerland', country_code2: 'CH' };
      }
      if (!cancelled) {
        setCatalogGeo(location);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { base, tiers } = useMemo(() => partitionPlans(plans), [plans]);

  const onboardingPriceBySlug = useMemo(() => {
    const m = new Map<string, string | null>();
    [...onboardingPlans]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((row) => {
        m.set(row.slug, formatOnboardingOneTime(row));
      });
    return m;
  }, [onboardingPlans, i18n.language]);

  const renderOnboardingOneTime = (slug: string) => {
    const formatted = onboardingPriceBySlug.get(slug);
    if (formatted) {
      return (
        <>
          {formatted} <S.SetupPriceOl>{t('changePlan.oneTime')}</S.SetupPriceOl>
        </>
      );
    }
    return t('changePlan.priceUnavailable');
  };

  const getPurchasedOnboarding = useCallback(
    (slug: string) => {
      const row = onboardingPlans.find((p) => p.slug === slug);
      const list = (subscription as UserSubscription | null | undefined)
        ?.onboardingPurchases;
      if (!list?.length) return undefined;
      for (const p of list) {
        if (p.slug === slug) return p;
        if (row && p.onboardingPlanId === row.id) return p;
      }
      return undefined;
    },
    [onboardingPlans, subscription],
  );

  const startOnboardingCheckout = useCallback(
    async (slug: string) => {
      if (getPurchasedOnboarding(slug)) return;
      const row = onboardingPlans.find((p) => p.slug === slug);
      if (!row?.id || !row.stripePriceIdOneTime) {
        toast.error(t('changePlan.onboardingCheckoutUnavailable'));
        return;
      }
      setOnboardingCheckoutSlug(slug);
      try {
        const { url } = await createOnboardingCheckoutSessionRequest({
          onboardingPlanId: row.id,
          ...buildStripeCheckoutPresentmentBody(catalogGeo),
        });
        window.location.href = url;
      } catch (err) {
        const status = axios.isAxiosError(err)
          ? err.response?.status
          : undefined;
        if (status === 409) {
          toast.error(t('changePlan.onboardingAlreadyPurchased'));
        } else {
          toast.error(t('changePlan.onboardingCheckoutFailed'));
        }
        setOnboardingCheckoutSlug(null);
      }
    },
    [catalogGeo, getPurchasedOnboarding, onboardingPlans, t],
  );

  const renderOnboardingCardFooter = useCallback(
    (slug: string, primary?: boolean) => {
      const purchased = getPurchasedOnboarding(slug);
      if (purchased) {
        const dateLabel = purchased.createdAt
          ? formatSubscriptionDate(purchased.createdAt, i18n.language)
          : '';
        return (
          <>
            <S.SetupPurchased>
              {t('changePlan.onboardingPurchasedTitle')}
            </S.SetupPurchased>
            {dateLabel ? (
              <S.SetupPurchasedSub>
                {t('changePlan.onboardingPurchasedOn', { date: dateLabel })}
              </S.SetupPurchasedSub>
            ) : null}
          </>
        );
      }
      const cta = (
        <S.SetupCta
          as="button"
          type="button"
          disabled={onboardingCheckoutSlug === slug}
          onClick={() => startOnboardingCheckout(slug)}
          style={{ fontFamily: 'inherit' }}
          {...(primary ? { $primary: true } : {})}
        >
          {t('changePlan.bookOnboarding')}
        </S.SetupCta>
      );
      return <>{cta}</>;
    },
    [
      getPurchasedOnboarding,
      i18n.language,
      onboardingCheckoutSlug,
      startOnboardingCheckout,
      t,
    ],
  );

  const onBasePaidPlan =
    Boolean(base && subscription) &&
    (subscription!.subscriptionPlanId === base!.id ||
      subscription!.subscriptionPlan?.id === base!.id);

  const subPlanId =
    subscription?.subscriptionPlanId ??
    subscription?.subscriptionPlan?.id ??
    '';

  const currentPremiumTierIdx = useMemo(() => {
    if (!subscription || tiers.length === 0) return -1;
    if (subPlanId) {
      const byId = tiers.findIndex((p) => p.id === subPlanId);
      if (byId >= 0) return byId;
    }
    const mc = subscription.maxCommunities;
    if (mc != null) {
      const byMc = tiers.findIndex((p) => p.maxCommunities === mc);
      if (byMc >= 0) return byMc;
    }
    return -1;
  }, [subscription, subPlanId, tiers]);

  const onPremiumPaidPlan =
    isPaidActive &&
    Boolean(base) &&
    currentPremiumTierIdx >= 0 &&
    !onBasePaidPlan;

  const showProUpgradeUx = isPaidActive && onBasePaidPlan && Boolean(base);

  /** One-time sync to subscription tier when entering premium; do not clamp user to current tier. */
  const premiumSliderSyncedRef = useRef(false);
  useEffect(() => {
    if (!onPremiumPaidPlan || currentPremiumTierIdx < 0) {
      premiumSliderSyncedRef.current = false;
      return;
    }
    const smax = Math.max(0, tiers.length - 1);
    if (!premiumSliderSyncedRef.current) {
      premiumSliderSyncedRef.current = true;
      setTierIdx(Math.min(Math.max(currentPremiumTierIdx, 0), smax));
      return;
    }
    setTierIdx((prev) => Math.min(Math.max(prev, 0), smax));
  }, [
    onPremiumPaidPlan,
    currentPremiumTierIdx,
    tiers.length,
    base,
    onBasePaidPlan,
    isPaidActive,
  ]);

  const sliderMin = 0;
  const sliderMax = Math.max(0, tiers.length - 1);
  const tierIdxClamped = Math.min(Math.max(tierIdx, sliderMin), sliderMax);
  const activeTierIdx = tierIdxClamped;
  const premiumTierDowngradePreview =
    onPremiumPaidPlan &&
    currentPremiumTierIdx >= 0 &&
    activeTierIdx < currentPremiumTierIdx;
  const selectedTier = tiers[activeTierIdx];
  const sliderSpan = sliderMax - sliderMin;
  const sliderPct =
    sliderSpan <= 0
      ? 0
      : Math.round(((activeTierIdx - sliderMin) / sliderSpan) * 100);

  useEffect(() => {
    dispatch(getCurrentUserSubscription.trigger());
  }, [dispatch]);

  const planHref = useCallback(
    (plan: SubscriptionPlan | undefined) => {
      if (!plan) return checkoutBase;
      const freq = yearly ? 'YEAR' : 'MONTH';
      return `${checkoutBase}/${plan.id}?frequency=${freq}`;
    },
    [checkoutBase, yearly],
  );

  const ownedCommunitiesList = useMemo(() => {
    if (!profile?.userId) return [];
    return communities
      .filter((c) => c.communityOwner?.userId === profile.userId)
      .map((c) => ({
        id: c.id,
        name: c.name ?? '',
        logo: c.logo ?? null,
        subscriptionSlotActive: c.subscriptionSlotActive,
      }));
  }, [communities, profile?.userId]);

  const onCommunitySlotsSaved = useCallback(
    async (selectedIds: string[]) => {
      if (!profile?.userId) return;
      setPendingCommunitySubscriptionSlots(profile.userId, selectedIds);
      const pending = pendingCheckoutRef.current;
      pendingCheckoutRef.current = null;
      if (pending) {
        navigate(pending.href, {
          state: { checkoutPlan: pending.plan },
        });
      }
    },
    [navigate, profile?.userId],
  );

  const onCommunitySlotsClose = useCallback(() => {
    setCommunitySlotsOpen(false);
    pendingCheckoutRef.current = null;
  }, []);

  const beginCheckout = useCallback(
    (plan: SubscriptionPlan) => {
      const href = planHref(plan);
      const limit = plan.maxCommunities ?? 0;
      if (limit > 0 && usedActiveCommunities > limit) {
        pendingCheckoutRef.current = { href, plan };
        setCommunitySlotsMax(limit);
        setCommunitySlotsOpen(true);
      } else {
        pendingCheckoutRef.current = null;
        navigate(href, { state: { checkoutPlan: plan } });
      }
    },
    [navigate, planHref, usedActiveCommunities],
  );

  const basePrice = base ? (yearly ? base.yearlyValue : base.monthlyValue) : 0;
  const tierPrice = selectedTier
    ? yearly
      ? selectedTier.yearlyValue
      : selectedTier.monthlyValue
    : 0;

  const baseMonthlyEq =
    base && yearly && base.yearlyValue ? base.yearlyValue / 12 : null;
  const tierMonthlyEq =
    selectedTier && yearly && selectedTier.yearlyValue
      ? selectedTier.yearlyValue / 12
      : null;

  const yearlySaveVsMonthly = selectedTier
    ? selectedTier.monthlyValue * 12 - selectedTier.yearlyValue
    : 0;

  const currency = base?.currency || 'USD';

  const baseFits = base ? planFits(base, used) : true;

  const recommendedPlan = useMemo(() => {
    if (!base) return null;
    const candidates = [base, ...tiers];
    const fitting = candidates.filter((p) => planFits(p, used));
    if (fitting.length === 0) return tiers[tiers.length - 1] || base;
    return fitting.reduce((best, p) =>
      p.yearlyValue < best.yearlyValue ? p : best,
    );
  }, [base, tiers, used]);

  const showTrialBanner = isTrial;

  const planDisplayName =
    subscription?.subscriptionPlan?.productName ||
    subscription?.subscriptionPlan?.name ||
    t('changePlan.tierPro');

  const renewalPriceForBanner = subscription?.subscriptionPlan
    ? subscription.frequency === SubscriptionFrequency.YEAR
      ? subscription.subscriptionPlan.yearlyValue
      : subscription.subscriptionPlan.monthlyValue
    : basePrice;

  const renewalPeriodForCopy =
    subscription?.frequency === SubscriptionFrequency.YEAR
      ? t('changePlan.perYear')
      : t('changePlan.perMonth');

  const communitiesAtLimit =
    maxCommunities > 0 && usedActiveCommunities >= maxCommunities;
  const membersAtLimit = maxMembers > 0 && usedMembers >= maxMembers;
  const groupsAtLimit = maxGroups > 0 && usedGroups >= maxGroups;
  const storageAtLimit = maxStorage > 0 && usedStorage >= maxStorage;

  const entSliderHighlight =
    tiers.length > 0 && activeTierIdx === sliderMax && sliderMax >= 6;

  useEffect(() => {
    if (isSubscriptionLoading) {
      setHasObservedSubscriptionLoad(true);
    }
  }, [isSubscriptionLoading]);

  const isSubscriptionPending =
    !subscription && (isSubscriptionLoading || !hasObservedSubscriptionLoad);

  if ((isPlansInitialLoading && !hasPlansData) || isSubscriptionPending) {
    return (
      <OutsideAppPageContentWrapper minHeight="100svh">
        <S.PageWrap>
          <Loader />
        </S.PageWrap>
      </OutsideAppPageContentWrapper>
    );
  }

  if (plans.length === 0) {
    return (
      <OutsideAppPageContentWrapper minHeight="100svh">
        <S.PageWrap>
          <Breadcrumb
            items={[
              {
                label: t('changePlan.breadcrumbSubscription'),
                to: subscriptionPath,
              },
              { label: t('changePlan.breadcrumbCurrent') },
            ]}
          />
          <S.PageHeader style={{ marginTop: 24 }}>
            <S.PageTitle>{t('changePlan.catalogEmptyTitle')}</S.PageTitle>
            <S.PageSubtitle>{t('changePlan.catalogEmptyBody')}</S.PageSubtitle>
          </S.PageHeader>
          <S.PlanCta as={Link} to={subscriptionPath} $variant="pri">
            {t('changePlan.backToSubscription')}
          </S.PlanCta>
        </S.PageWrap>
      </OutsideAppPageContentWrapper>
    );
  }

  return (
    <OutsideAppPageContentWrapper minHeight="100svh">
      <S.PageWrap>
        <Breadcrumb
          items={[
            {
              label: t('changePlan.breadcrumbSubscription'),
              to: subscriptionPath,
            },
            {
              label: showProUpgradeUx
                ? t('changePlan.breadcrumbUpgrade')
                : t('changePlan.breadcrumbCurrent'),
            },
          ]}
        />

        {showTrialBanner ? (
          <S.TrialBanner>
            <S.TrialBannerIcon aria-hidden>⏳</S.TrialBannerIcon>
            <S.TrialBannerTextBlock>
              <S.TrialBannerTitle>
                {t('changePlan.trialTitle', { date: subscriptionEndDate })}
              </S.TrialBannerTitle>
              <S.TrialBannerSub>{t('changePlan.trialBody')}</S.TrialBannerSub>
            </S.TrialBannerTextBlock>
          </S.TrialBanner>
        ) : null}

        {showProUpgradeUx ? (
          <S.ProActiveBanner>
            <S.ProActiveDot aria-hidden />
            <S.ProActiveBannerText>
              <strong>
                {t('changePlan.proActiveLead', { plan: planDisplayName })}
              </strong>{' '}
              {renewalDateFormatted
                ? t('changePlan.proActiveTrail', {
                    date: renewalDateFormatted,
                    price: formatMoney(currency, renewalPriceForBanner),
                    period: renewalPeriodForCopy,
                  })
                : t('changePlan.proActiveTrailNoDate', {
                    price: formatMoney(currency, renewalPriceForBanner),
                    period: renewalPeriodForCopy,
                  })}
            </S.ProActiveBannerText>
          </S.ProActiveBanner>
        ) : onPremiumPaidPlan ? (
          <S.ProActiveBanner>
            <S.ProActiveDot aria-hidden />
            <S.ProActiveBannerText>
              <strong>{t('changePlan.premiumActiveLead')}</strong>{' '}
              {renewalDateFormatted
                ? t('changePlan.premiumActiveTrail', {
                    teams: maxCommunities,
                    date: renewalDateFormatted,
                    price: formatMoney(currency, renewalPriceForBanner),
                    period: renewalPeriodForCopy,
                  })
                : t('changePlan.premiumActiveTrailNoDate', {
                    teams: maxCommunities,
                    price: formatMoney(currency, renewalPriceForBanner),
                    period: renewalPeriodForCopy,
                  })}
            </S.ProActiveBannerText>
          </S.ProActiveBanner>
        ) : null}

        <S.UsageBox>
          <S.UsageBoxHeader>
            <S.UsageBoxHeaderIntro>
              <S.UsageBoxTitle>{t('changePlan.usageTitle')}</S.UsageBoxTitle>
              <S.UsageBoxSubtitle>
                {isTrial
                  ? t('changePlan.usageSubtitle')
                  : showProUpgradeUx
                  ? t('changePlan.usageSubtitlePro')
                  : onPremiumPaidPlan
                  ? t('changePlan.usageSubtitlePremium', {
                      teams: maxCommunities,
                    })
                  : t('changePlan.usageSubtitleGeneral')}
              </S.UsageBoxSubtitle>
            </S.UsageBoxHeaderIntro>
            {isTrial ? (
              <S.PillBadge>{t('changePlan.usageTrialBadge')}</S.PillBadge>
            ) : showProUpgradeUx ? (
              <S.UsageProBadge>{t('changePlan.usageProBadge')}</S.UsageProBadge>
            ) : onPremiumPaidPlan ? (
              <S.UsageProBadge>
                {t('changePlan.usagePremiumBadge', { teams: maxCommunities })}
              </S.UsageProBadge>
            ) : null}
          </S.UsageBoxHeader>
          <S.UsageMetricsGrid>
            <S.UsageMetricBlock>
              <S.UsageMetricTop>
                <S.UsageMetricLabel>
                  <S.UsageMetricIcon aria-hidden>
                    {USAGE_METRIC_ICONS.members}
                  </S.UsageMetricIcon>
                  {t('changePlan.usageMembers')}
                </S.UsageMetricLabel>
                <S.UsageMetricValue
                  $amber={
                    showProUpgradeUx &&
                    pct(usedMembers, maxMembers) >= 70 &&
                    maxMembers > 0
                  }
                >
                  {usedMembers}/{maxMembers}
                </S.UsageMetricValue>
              </S.UsageMetricTop>
              <S.BarTrack>
                <S.BarFill
                  $pct={pct(usedMembers, maxMembers)}
                  $amber={
                    showProUpgradeUx &&
                    pct(usedMembers, maxMembers) >= 70 &&
                    maxMembers > 0
                  }
                />
              </S.BarTrack>
              {(showProUpgradeUx || onPremiumPaidPlan) && selectedTier ? (
                <>
                  <S.UsageMutedHint>
                    {t('changePlan.spotsRemaining', {
                      count: Math.max(0, maxMembers - usedMembers),
                    })}
                  </S.UsageMutedHint>
                  {usedMembers <= selectedTier.maxMembers ? (
                    <S.UsageFitHint>
                      {t('changePlan.usageProPremiumFitsMembers', {
                        max: selectedTier.maxMembers.toLocaleString(),
                      })}
                    </S.UsageFitHint>
                  ) : null}
                </>
              ) : membersAtLimit ? (
                <S.UsageLimitHint>
                  {t('changePlan.usageHintMembersAtLimit')}
                </S.UsageLimitHint>
              ) : (
                <S.UsageHint>{t('changePlan.usageHintMembers')}</S.UsageHint>
              )}
            </S.UsageMetricBlock>
            <S.UsageMetricBlock>
              <S.UsageMetricTop>
                <S.UsageMetricLabel>
                  <S.UsageMetricIcon aria-hidden>
                    {USAGE_METRIC_ICONS.communities}
                  </S.UsageMetricIcon>
                  {t('changePlan.usageCommunities')}
                </S.UsageMetricLabel>
                <S.UsageMetricValue
                  $warn={
                    usedActiveCommunities >= maxCommunities &&
                    maxCommunities > 0
                  }
                >
                  {usedActiveCommunities}/{maxCommunities}
                </S.UsageMetricValue>
              </S.UsageMetricTop>
              <S.BarTrack>
                <S.BarFill
                  $pct={pct(usedActiveCommunities, maxCommunities)}
                  $warn={
                    usedActiveCommunities >= maxCommunities &&
                    maxCommunities > 0
                  }
                />
              </S.BarTrack>
              {(showProUpgradeUx || onPremiumPaidPlan) && selectedTier ? (
                <>
                  {communitiesAtLimit ? (
                    <S.UsageLimitHint>
                      {t('changePlan.usageProAtLimitCommunities')}
                    </S.UsageLimitHint>
                  ) : null}
                  {selectedTier.maxCommunities > maxCommunities ? (
                    <S.UsageFitHint>
                      {t('changePlan.usageProPremiumFitsCommunities', {
                        teams: selectedTier.maxCommunities,
                      })}
                    </S.UsageFitHint>
                  ) : null}
                </>
              ) : communitiesAtLimit ? (
                <S.UsageLimitHint>
                  {t('changePlan.usageHintCommunitiesAtLimit')}
                </S.UsageLimitHint>
              ) : (
                <S.UsageHint>
                  {t('changePlan.usageHintCommunities')}
                </S.UsageHint>
              )}
            </S.UsageMetricBlock>
            <S.UsageMetricBlock>
              <S.UsageMetricTop>
                <S.UsageMetricLabel>
                  <S.UsageMetricIcon aria-hidden>
                    {USAGE_METRIC_ICONS.schedulers}
                  </S.UsageMetricIcon>
                  {t('changePlan.usageSchedulers')}
                </S.UsageMetricLabel>
                <S.UsageMetricValue>
                  {usedGroups}/{maxGroups}
                </S.UsageMetricValue>
              </S.UsageMetricTop>
              <S.BarTrack>
                <S.BarFill $pct={pct(usedGroups, maxGroups)} />
              </S.BarTrack>
              {(showProUpgradeUx || onPremiumPaidPlan) && selectedTier ? (
                <>
                  <S.UsageMutedHint>
                    {t('changePlan.schedulersRemaining', {
                      count: Math.max(0, maxGroups - usedGroups),
                    })}
                  </S.UsageMutedHint>
                  {usedGroups <= selectedTier.maxGroups ? (
                    <S.UsageFitHint>
                      {t('changePlan.usageProPremiumFitsSchedulers', {
                        count: selectedTier.maxGroups,
                      })}
                    </S.UsageFitHint>
                  ) : null}
                </>
              ) : groupsAtLimit ? (
                <S.UsageLimitHint>
                  {t('changePlan.usageHintSchedulersAtLimit')}
                </S.UsageLimitHint>
              ) : (
                <S.UsageHint>{t('changePlan.usageHintSchedulers')}</S.UsageHint>
              )}
            </S.UsageMetricBlock>
            <S.UsageMetricBlock>
              <S.UsageMetricTop>
                <S.UsageMetricLabel>
                  <S.UsageMetricIcon aria-hidden>
                    {USAGE_METRIC_ICONS.storage}
                  </S.UsageMetricIcon>
                  {t('changePlan.usageStorage')}
                </S.UsageMetricLabel>
                <S.UsageMetricValue>
                  {usedStorage.toFixed(1)} GB/{maxStorage} GB
                </S.UsageMetricValue>
              </S.UsageMetricTop>
              <S.BarTrack>
                <S.BarFill $pct={pct(usedStorage, maxStorage)} />
              </S.BarTrack>
              {(showProUpgradeUx || onPremiumPaidPlan) && selectedTier ? (
                <>
                  <S.UsageMutedHint>
                    {t('changePlan.storageRemaining', {
                      count: Math.max(
                        0,
                        Math.round((maxStorage - usedStorage) * 10) / 10,
                      ),
                    })}
                  </S.UsageMutedHint>
                  {usedStorage <= selectedTier.maxStorage ? (
                    <S.UsageFitHint>
                      {t('changePlan.usageProPremiumFitsStorage', {
                        gb: selectedTier.maxStorage,
                      })}
                    </S.UsageFitHint>
                  ) : null}
                </>
              ) : storageAtLimit ? (
                <S.UsageLimitHint>
                  {t('changePlan.usageHintStorageAtLimit')}
                </S.UsageLimitHint>
              ) : (
                <S.UsageHint>{t('changePlan.usageHintStorage')}</S.UsageHint>
              )}
            </S.UsageMetricBlock>
          </S.UsageMetricsGrid>

          {showProUpgradeUx && communitiesAtLimit ? (
            <S.CommunitiesNudge>
              <strong>{t('changePlan.communitiesLimitNudgeTitle')}</strong>{' '}
              {t('changePlan.communitiesLimitNudgeBody')}
            </S.CommunitiesNudge>
          ) : !baseFits && base && recommendedPlan ? (
            <S.NudgeBox>
              {t('changePlan.nudgeIntro', {
                plan: base.productName || base.name,
              })}{' '}
              {t('changePlan.nudgeReco', {
                plan: recommendedPlan.productName || recommendedPlan.name,
                price: formatMoney(currency, recommendedPlan.yearlyValue),
              })}
            </S.NudgeBox>
          ) : null}
        </S.UsageBox>

        <S.PageHeader>
          <S.PageTitle>
            {showProUpgradeUx
              ? t('changePlan.pageTitleUpgrade')
              : onPremiumPaidPlan
              ? t('changePlan.pageTitlePremium')
              : t('changePlan.pageTitle')}
          </S.PageTitle>
          <S.PageSubtitle>
            {showProUpgradeUx
              ? t('changePlan.pageSubtitleUpgrade')
              : onPremiumPaidPlan
              ? t('changePlan.pageSubtitlePremium', {
                  teams: maxCommunities,
                })
              : t('changePlan.pageSubtitle')}
          </S.PageSubtitle>
        </S.PageHeader>

        <S.PeriodRow>
          <S.PeriodToggle>
            <S.PeriodBtn
              type="button"
              $active={!yearly}
              onClick={() => setYearly(false)}
            >
              {t('changePlan.monthly')}
            </S.PeriodBtn>
            <S.PeriodBtn
              type="button"
              $active={yearly}
              onClick={() => setYearly(true)}
            >
              {t('changePlan.yearly')}
            </S.PeriodBtn>
          </S.PeriodToggle>
          {yearly ? (
            <S.SaveBadge>{t('changePlan.saveBadge')}</S.SaveBadge>
          ) : null}
        </S.PeriodRow>

        <S.PlansGrid>
          {base ? (
            <S.PlanCard
              $isCurrent={showProUpgradeUx}
              $isDowngrade={onPremiumPaidPlan}
              style={{ marginTop: 13 }}
            >
              {showProUpgradeUx ? (
                <S.CurrentPlanBadge>
                  {t('changePlan.currentPlanBadge')}
                </S.CurrentPlanBadge>
              ) : null}
              <S.PlanTier style={{ marginTop: showProUpgradeUx ? 10 : 0 }}>
                {t('changePlan.tierPro')}
              </S.PlanTier>
              <S.PlanName>{base.productName || base.name}</S.PlanName>
              <S.PlanTagline>{t('changePlan.proTagline')}</S.PlanTagline>
              {onPremiumPaidPlan ? (
                <S.DeltaDownBadge>
                  {t('changePlan.deltaDowngrade')}
                </S.DeltaDownBadge>
              ) : null}
              <S.PlanPrice>
                <S.PlanPriceSup>
                  {planPriceLeadingSymbol(currency)}
                </S.PlanPriceSup>
                {formatMoneyAmount(currency, basePrice)}
                <S.PlanPricePeriod>
                  {yearly ? t('changePlan.perYear') : t('changePlan.perMonth')}
                </S.PlanPricePeriod>
              </S.PlanPrice>
              <S.PlanMonthlyEq>
                {yearly && baseMonthlyEq
                  ? t('changePlan.approxPerMonth', {
                      price: formatMoney(currency, baseMonthlyEq),
                    })
                  : yearly
                  ? ''
                  : t('changePlan.equalsPerYear', {
                      price: formatMoney(currency, base.monthlyValue * 12),
                    })}
              </S.PlanMonthlyEq>
              <S.SpecRow>
                <S.SpecPill $current={showProUpgradeUx}>
                  <span>🏠</span>
                  <span>
                    <S.SpecVal $current={showProUpgradeUx}>
                      {base.maxCommunities}
                    </S.SpecVal>{' '}
                    {t('changePlan.specCommunities')}
                  </span>
                </S.SpecPill>
                <S.SpecPill $current={showProUpgradeUx}>
                  <span>👥</span>
                  <span>
                    {t('changePlan.upTo')}{' '}
                    <S.SpecVal $current={showProUpgradeUx}>
                      {base.maxMembers}
                    </S.SpecVal>{' '}
                    {t('changePlan.specMembers')}
                  </span>
                </S.SpecPill>
                {/* <S.SpecPill $current={showProUpgradeUx}>
                  <span>📋</span>
                  <span>
                    <S.SpecVal $current={showProUpgradeUx}>
                      {base.maxGroups}
                    </S.SpecVal>{' '}
                    {t('changePlan.specSchedulers')}
                  </span>
                </S.SpecPill> */}
                <S.SpecPill $current={showProUpgradeUx}>
                  <span>💾</span>
                  <span>
                    <S.SpecVal $current={showProUpgradeUx}>
                      {base.maxStorage} GB
                    </S.SpecVal>{' '}
                    {t('changePlan.specStorage')}
                  </span>
                </S.SpecPill>
              </S.SpecRow>
              <S.Divider />
              <S.FeatureList>
                {(
                  t('changePlan.proFeatures', {
                    returnObjects: true,
                  }) as string[]
                ).map((line) => (
                  <S.FeatureLi key={line}>
                    <S.FiCheck aria-hidden>✓</S.FiCheck>
                    <S.FeatureItemText>{line}</S.FeatureItemText>
                  </S.FeatureLi>
                ))}
              </S.FeatureList>
              {showProUpgradeUx ? (
                <>
                  <S.PlanCtaDisabled>
                    {t('changePlan.currentPlanCta')}
                  </S.PlanCtaDisabled>
                  <S.PlanGuarantee>
                    {renewalDateFormatted
                      ? t('changePlan.activeRenewsNote', {
                          date: renewalDateFormatted,
                        })
                      : t('changePlan.activePlanFoot')}
                  </S.PlanGuarantee>
                </>
              ) : (
                <>
                  <S.PlanCta
                    as="button"
                    type="button"
                    onClick={() => beginCheckout(base)}
                    $variant="sec"
                  >
                    {onPremiumPaidPlan
                      ? t('changePlan.downgradeToPro')
                      : t('changePlan.subscribePro')}
                  </S.PlanCta>
                  <S.PlanGuarantee>
                    {t('changePlan.ctaFootnote')}
                  </S.PlanGuarantee>
                </>
              )}
            </S.PlanCard>
          ) : null}

          <S.PlanCard
            $featured={!onPremiumPaidPlan}
            $isCurrent={onPremiumPaidPlan}
            $isDowngrade={premiumTierDowngradePreview}
            style={{ marginTop: 13 }}
          >
            {onPremiumPaidPlan ? (
              <S.CurrentPlanBadge>
                {t('changePlan.currentPlanBadge')}
              </S.CurrentPlanBadge>
            ) : showProUpgradeUx ? (
              <S.PopularBadge>
                {t('changePlan.recommendedUpgrade')}
              </S.PopularBadge>
            ) : tiers.length > 0 ? (
              <S.PopularBadge>{t('changePlan.mostPopular')}</S.PopularBadge>
            ) : null}
            <S.PlanTier
              style={{
                marginTop: onPremiumPaidPlan || showProUpgradeUx ? 10 : 0,
              }}
            >
              {t('changePlan.tierPremium')}
            </S.PlanTier>
            <S.PlanName>{t('changePlan.premiumTitle')}</S.PlanName>
            <S.PlanTagline>{t('changePlan.premiumTagline')}</S.PlanTagline>
            {showProUpgradeUx ? (
              <S.DeltaUpBadge>{t('changePlan.upgradeFromPro')}</S.DeltaUpBadge>
            ) : null}
            {selectedTier ? (
              <>
                <S.PlanPrice>
                  <S.PlanPriceSup>
                    {planPriceLeadingSymbol(currency)}
                  </S.PlanPriceSup>
                  {yearly
                    ? formatMoneyAmount(currency, selectedTier.yearlyValue)
                    : formatMoneyAmount(currency, selectedTier.monthlyValue)}
                  <S.PlanPricePeriod>
                    {yearly
                      ? t('changePlan.perYear')
                      : t('changePlan.perMonth')}
                  </S.PlanPricePeriod>
                </S.PlanPrice>
                <S.PlanMonthlyEq>
                  {yearly && tierMonthlyEq
                    ? t('changePlan.approxPerMonth', {
                        price: formatMoney(currency, tierMonthlyEq),
                      })
                    : !yearly
                    ? t('changePlan.equalsPerYear', {
                        price: formatMoney(
                          currency,
                          selectedTier.monthlyValue * 12,
                        ),
                      })
                    : ''}
                </S.PlanMonthlyEq>
                {yearly && yearlySaveVsMonthly > 0 ? (
                  <S.SavingsBadge>
                    {t('changePlan.saveVsMonthly', {
                      amount: formatMoney(currency, yearlySaveVsMonthly),
                    })}
                  </S.SavingsBadge>
                ) : null}
              </>
            ) : (
              <>
                <S.CustomPriceTitle>
                  {t('changePlan.premiumPlaceholderTitle')}
                </S.CustomPriceTitle>
                <S.PlanMonthlyEq>
                  {t('changePlan.premiumPlaceholderSub')}
                </S.PlanMonthlyEq>
              </>
            )}

            {tiers.length > 0 ? (
              <S.PremSliderBlock>
                <S.PremSliderHeader>
                  <S.PremSliderLabel>
                    {onPremiumPaidPlan
                      ? activeTierIdx === currentPremiumTierIdx
                        ? t('changePlan.sliderLabelCurrentTier')
                        : t('changePlan.sliderLabelSelectedTier')
                      : t('changePlan.sliderLabel')}
                  </S.PremSliderLabel>
                  <S.PremMemberDisplay>
                    {selectedTier?.maxCommunities ?? '-'}{' '}
                    {t('changePlan.teams')}
                  </S.PremMemberDisplay>
                </S.PremSliderHeader>
                <S.PremSlider
                  type="range"
                  min={sliderMin}
                  max={sliderMax}
                  value={activeTierIdx}
                  step={1}
                  onChange={(e) => {
                    setTierIdx(Number(e.target.value));
                  }}
                  style={
                    {
                      ['--slider-fill-pct' as string]: `${sliderPct}%`,
                    } as React.CSSProperties
                  }
                />
                <S.PremSliderTicks>
                  {tiers.map((p, i) => (
                    <S.PremTick
                      key={p.id}
                      $active={i === activeTierIdx}
                      $index={i}
                      $count={tiers.length}
                    >
                      {p.maxCommunities}
                    </S.PremTick>
                  ))}
                </S.PremSliderTicks>
                <S.PremExtraNote
                  $visible={activeTierIdx === sliderMax && sliderMax >= 6}
                >
                  {t('changePlan.sliderEnterpriseHint')}
                </S.PremExtraNote>
              </S.PremSliderBlock>
            ) : null}

            {selectedTier ? (
              <S.SpecRow style={{ marginTop: 12 }}>
                <S.SpecPill
                  $active={
                    !onPremiumPaidPlan ||
                    activeTierIdx !== currentPremiumTierIdx
                  }
                  $current={
                    onPremiumPaidPlan && activeTierIdx === currentPremiumTierIdx
                  }
                >
                  <span>🏠</span>
                  <span>
                    <S.SpecVal
                      $active={
                        !onPremiumPaidPlan ||
                        activeTierIdx !== currentPremiumTierIdx
                      }
                      $current={
                        onPremiumPaidPlan &&
                        activeTierIdx === currentPremiumTierIdx
                      }
                    >
                      {selectedTier.maxCommunities}
                    </S.SpecVal>{' '}
                    {t('changePlan.specCommunities')}
                  </span>
                </S.SpecPill>
                <S.SpecPill
                  $active={
                    !onPremiumPaidPlan ||
                    activeTierIdx !== currentPremiumTierIdx
                  }
                  $current={
                    onPremiumPaidPlan && activeTierIdx === currentPremiumTierIdx
                  }
                >
                  <span>👥</span>
                  <span>
                    {t('changePlan.upTo')}{' '}
                    <S.SpecVal
                      $active={
                        !onPremiumPaidPlan ||
                        activeTierIdx !== currentPremiumTierIdx
                      }
                      $current={
                        onPremiumPaidPlan &&
                        activeTierIdx === currentPremiumTierIdx
                      }
                    >
                      {selectedTier.maxMembers}
                    </S.SpecVal>{' '}
                    {t('changePlan.specMembers')}
                  </span>
                </S.SpecPill>
                {/* <S.SpecPill
                  $active={
                    !onPremiumPaidPlan || activeTierIdx !== currentPremiumTierIdx
                  }
                  $current={
                    onPremiumPaidPlan && activeTierIdx === currentPremiumTierIdx
                  }
                >
                  <span>📋</span>
                  <span>
                    <S.SpecVal
                      $active={
                        !onPremiumPaidPlan ||
                        activeTierIdx !== currentPremiumTierIdx
                      }
                      $current={
                        onPremiumPaidPlan &&
                        activeTierIdx === currentPremiumTierIdx
                      }
                    >
                      {selectedTier.maxGroups}
                    </S.SpecVal>{' '}
                    {t('changePlan.specSchedulers')}
                  </span>
                </S.SpecPill> */}
                <S.SpecPill
                  $active={
                    !onPremiumPaidPlan ||
                    activeTierIdx !== currentPremiumTierIdx
                  }
                  $current={
                    onPremiumPaidPlan && activeTierIdx === currentPremiumTierIdx
                  }
                >
                  <span>💾</span>
                  <span>
                    <S.SpecVal
                      $active={
                        !onPremiumPaidPlan ||
                        activeTierIdx !== currentPremiumTierIdx
                      }
                      $current={
                        onPremiumPaidPlan &&
                        activeTierIdx === currentPremiumTierIdx
                      }
                    >
                      {selectedTier.maxStorage} GB
                    </S.SpecVal>{' '}
                    {t('changePlan.specStorage')}
                  </span>
                </S.SpecPill>
              </S.SpecRow>
            ) : null}

            <S.Divider />
            <S.FeatureList>
              {(
                t('changePlan.premiumFeatures', {
                  returnObjects: true,
                }) as string[]
              ).map((line, i) => (
                <S.FeatureLi key={line}>
                  <S.FiCheck aria-hidden>✓</S.FiCheck>
                  <S.FeatureItemText>
                    {i === 0 ? <strong>{line}</strong> : line}
                  </S.FeatureItemText>
                </S.FeatureLi>
              ))}
            </S.FeatureList>
            {selectedTier ? (
              onPremiumPaidPlan && activeTierIdx === currentPremiumTierIdx ? (
                <>
                  <S.PlanCtaDisabled>
                    {t('changePlan.currentPlanCta')}
                  </S.PlanCtaDisabled>
                  <S.PlanGuarantee>
                    {renewalDateFormatted
                      ? t('changePlan.activeRenewsNote', {
                          date: renewalDateFormatted,
                        })
                      : t('changePlan.activePlanFoot')}
                  </S.PlanGuarantee>
                </>
              ) : (
                <S.PlanCta
                  as="button"
                  type="button"
                  onClick={() => beginCheckout(selectedTier)}
                  $variant="pri"
                >
                  {showProUpgradeUx
                    ? t('changePlan.upgradePremiumCta', {
                        price: formatMoney(currency, tierPrice),
                        period: yearly
                          ? t('changePlan.yr')
                          : t('changePlan.mo'),
                      })
                    : onPremiumPaidPlan
                    ? t(
                        premiumTierDowngradePreview
                          ? 'changePlan.downgradePremiumTierCta'
                          : 'changePlan.upgradePremiumTierCta',
                        {
                          price: formatMoney(currency, tierPrice),
                          period: yearly
                            ? t('changePlan.yr')
                            : t('changePlan.mo'),
                        },
                      )
                    : t('changePlan.subscribePremium', {
                        price: formatMoney(currency, tierPrice),
                        period: yearly
                          ? t('changePlan.yr')
                          : t('changePlan.mo'),
                      })}
                </S.PlanCta>
              )
            ) : (
              <S.PlanCta
                as="button"
                type="button"
                onClick={() => beginCheckout(base)}
                $variant="pri"
              >
                {t('changePlan.viewPlans')}
              </S.PlanCta>
            )}
            {!(onPremiumPaidPlan && activeTierIdx === currentPremiumTierIdx) ? (
              <S.PlanGuarantee>
                {showProUpgradeUx || onPremiumPaidPlan
                  ? t('changePlan.prorationFootnote')
                  : t('changePlan.ctaFootnote')}
              </S.PlanGuarantee>
            ) : null}
          </S.PlanCard>

          <S.PlanCard
            $entHighlight={entSliderHighlight}
            style={{ marginTop: 13 }}
          >
            <S.PlanTier>{t('changePlan.tierEnterprise')}</S.PlanTier>
            <S.PlanName>{t('changePlan.enterpriseTitle')}</S.PlanName>
            <S.PlanTagline>{t('changePlan.enterpriseTagline')}</S.PlanTagline>
            <S.CustomPriceTitle>
              {t('changePlan.enterprisePrice')}
            </S.CustomPriceTitle>
            <S.PlanMonthlyEq>{t('changePlan.enterpriseSub')}</S.PlanMonthlyEq>
            <S.SpecRow>
              <S.SpecPill>
                <span>👥</span>
                <span>
                  <S.SpecVal>{t('changePlan.unlimited')}</S.SpecVal>{' '}
                  {t('changePlan.specMembers')}
                </span>
              </S.SpecPill>
              <S.SpecPill>
                <span>🏠</span>
                <span>
                  <S.SpecVal>{t('changePlan.unlimited')}</S.SpecVal>{' '}
                  {t('changePlan.specCommunitiesMany')}
                </span>
              </S.SpecPill>
              <S.SpecPill>
                <span>📋</span>
                <span>
                  <S.SpecVal>{t('changePlan.unlimited')}</S.SpecVal>{' '}
                  {t('changePlan.specSchedulersMany')}
                </span>
              </S.SpecPill>
              <S.SpecPill>
                <span>💾</span>
                <span>
                  <S.SpecVal>{t('changePlan.custom')}</S.SpecVal>{' '}
                  {t('changePlan.specStorage')}
                </span>
              </S.SpecPill>
            </S.SpecRow>
            <S.Divider />
            <S.FeatureList>
              {(
                t('changePlan.enterpriseFeatures', {
                  returnObjects: true,
                }) as string[]
              ).map((line, i) => (
                <S.FeatureLi key={line}>
                  <S.FiCheck aria-hidden>✓</S.FiCheck>
                  <S.FeatureItemText>
                    {i === 0 ? <strong>{line}</strong> : line}
                  </S.FeatureItemText>
                </S.FeatureLi>
              ))}
            </S.FeatureList>
            <S.PlanCta
              as={Link}
              $variant="og"
              $entPulse={entSliderHighlight}
              to={enterpriseCheckoutPath}
            >
              {t('changePlan.talkToUs')}
            </S.PlanCta>
            <S.PlanGuarantee>{t('changePlan.enterpriseFoot')}</S.PlanGuarantee>
          </S.PlanCard>
        </S.PlansGrid>

        <S.PlanDivider>
          <S.PlanDividerInner>
            <S.PlanDividerTitle>
              {t('changePlan.onboardDividerTitle')}
            </S.PlanDividerTitle>
            <S.PlanDividerText>
              {t('changePlan.onboardDividerBody')}
            </S.PlanDividerText>
            <S.DividerPills>
              <S.DividerPill>
                <span style={{ color: '#00b8a8' }}>✓</span>
                {t('changePlan.pillOptional')}
              </S.DividerPill>
              <S.DividerPill>
                <span style={{ color: '#00b8a8' }}>✓</span>
                {t('changePlan.pillNoSub')}
              </S.DividerPill>
            </S.DividerPills>
          </S.PlanDividerInner>
        </S.PlanDivider>

        <S.SetupWrap>
          <S.SectionLabel>
            <S.SectionNum $accent>2</S.SectionNum>
            <S.SectionLabelText>
              <h2>{t('changePlan.onboardSectionTitle')}</h2>
              <p>{t('changePlan.onboardSectionSub')}</p>
            </S.SectionLabelText>
          </S.SectionLabel>
          <S.SetupGrid>
            <S.SetupCard>
              <S.SetupTag $variant="new">{t('changePlan.setupNew')}</S.SetupTag>
              <S.SetupName>{t('changePlan.kickstartTitle')}</S.SetupName>
              <S.SetupTagline>{t('changePlan.kickstartTag')}</S.SetupTagline>
              <S.SetupPrice>
                {renderOnboardingOneTime('kickstart')}
              </S.SetupPrice>
              <S.SetupNote>{t('changePlan.kickstartNote')}</S.SetupNote>
              <S.SetupSectionLabel>
                {t('changePlan.whatsIncluded')}
              </S.SetupSectionLabel>
              <S.SetupList>
                {(
                  t('changePlan.kickstartBullets', {
                    returnObjects: true,
                  }) as string[]
                ).map((b) => (
                  <S.SetupLi key={b}>
                    <S.SetupLiCheck aria-hidden>✓</S.SetupLiCheck>
                    <SetupBullet text={b} />
                  </S.SetupLi>
                ))}
              </S.SetupList>
              {renderOnboardingCardFooter('kickstart')}
            </S.SetupCard>

            <S.SetupCard $featured>
              <S.SetupPopular>{t('changePlan.setupPopular')}</S.SetupPopular>
              <S.SetupName style={{ marginTop: 12 }}>
                {t('changePlan.fullServiceTitle')}
              </S.SetupName>
              <S.SetupTagline>{t('changePlan.fullServiceTag')}</S.SetupTagline>
              <S.SetupPrice>
                {renderOnboardingOneTime('full_service')}
              </S.SetupPrice>
              <S.SetupNote>{t('changePlan.fullServiceNote')}</S.SetupNote>
              <S.SetupSectionLabel>
                {t('changePlan.whatsIncluded')}
              </S.SetupSectionLabel>
              <S.SetupList>
                {(
                  t('changePlan.fullServiceBullets', {
                    returnObjects: true,
                  }) as string[]
                ).map((b) => (
                  <S.SetupLi key={b}>
                    <S.SetupLiCheck aria-hidden>✓</S.SetupLiCheck>
                    <SetupBullet text={b} />
                  </S.SetupLi>
                ))}
              </S.SetupList>
              {renderOnboardingCardFooter('full_service', true)}
            </S.SetupCard>

            <S.SetupCard>
              <S.SetupName style={{ marginTop: 12 }}>
                {t('changePlan.entOnboardTitle')}
              </S.SetupName>
              <S.SetupTagline>{t('changePlan.entOnboardTag')}</S.SetupTagline>
              <S.SetupPrice>
                {renderOnboardingOneTime('enterprise_onboarding')}
              </S.SetupPrice>
              <S.SetupNote>{t('changePlan.entOnboardNote')}</S.SetupNote>
              <S.SetupSectionLabel>
                {t('changePlan.entOnboardPlus')}
              </S.SetupSectionLabel>
              <S.SetupList>
                {(
                  t('changePlan.entOnboardBullets', {
                    returnObjects: true,
                  }) as string[]
                ).map((b) => (
                  <S.SetupLi key={b}>
                    <S.SetupLiCheck aria-hidden>✓</S.SetupLiCheck>
                    <SetupBullet text={b} />
                  </S.SetupLi>
                ))}
              </S.SetupList>
              {renderOnboardingCardFooter('enterprise_onboarding')}
            </S.SetupCard>
          </S.SetupGrid>
        </S.SetupWrap>

        <FeatureMatrix />
      </S.PageWrap>
      <CommunitySlotsModal
        isOpen={communitySlotsOpen}
        onClose={onCommunitySlotsClose}
        maxSlots={communitySlotsMax}
        userId={profile?.userId ?? ''}
        communities={ownedCommunitiesList}
        savedSelection={
          profile?.userId
            ? getPendingCommunitySubscriptionSlots(profile.userId)
            : null
        }
        onSaved={onCommunitySlotsSaved}
      />
    </OutsideAppPageContentWrapper>
  );
};

export default ChangePlanPage;
